/* eslint-disable-next-line no-restricted-imports */
import type { CategoryOption, StoreCategory } from '@jane/shared/models';

const KEY_SEPARATOR = '/';

/**
 * Build a selectionMap key from raw path segments. Each segment
 * is encoded to avoid collisions with the separator `/`
 * (e.g. root_subtype "Syringes/Tankers")
 */
export const encodeKey = (segments: string[]): string =>
  segments.map(encodeURIComponent).join(KEY_SEPARATOR);

/** Inverse of {@link encodeKey}: split a key back into its raw segments. */
export const decodeKey = (key: string): string[] =>
  key.split(KEY_SEPARATOR).map(decodeURIComponent);

/**
 * Group keys under each of its ancestors, so a node can look up its
 * descendants in O(1). Built once per selectionMap change.
 * @example ['a/b/c'] -> { 'a' => ['a/b/c'], 'a/b' => ['a/b/c'] }
 */
export const groupKeysByAncestor = (
  keys: Iterable<string>
): Map<string, string[]> => {
  const descendantsByKey = new Map<string, string[]>();

  for (const key of keys) {
    const segments = key.split(KEY_SEPARATOR);

    for (let depth = 1; depth < segments.length; depth++) {
      const ancestor = segments.slice(0, depth).join(KEY_SEPARATOR);
      const descendants = descendantsByKey.get(ancestor);

      if (descendants) {
        descendants.push(key);
      } else {
        descendantsByKey.set(ancestor, [key]);
      }
    }
  }

  return descendantsByKey;
};

// shape the data from the b/e to play nicely with reefer
export const transformData = (data: StoreCategory[]): CategoryOption[] => {
  return data.map((category) => ({
    label: category.kind,
    value: category.kind,
    subItems: category.subtypes
      .filter(
        (subtype) =>
          subtype.subtype &&
          (subtype.subtype === '(Subcategory not set)'
            ? subtype.brand_subtypes.length
            : true)
      )
      .map((subtype) => ({
        label: subtype.subtype,
        value: subtype.subtype,
        subItems: subtype.brand_subtypes.map((brandSubtype) => ({
          label: brandSubtype,
          value: brandSubtype,
        })),
      })),
  }));
};

// Remove duplicates due to lowercasing bug
const removeDuplicates = (sortedKeys: string[]) => {
  return sortedKeys.reduce<string[]>(
    (prevValue: string[], key: string, index: number) => {
      // See if there's a duplicate without case sensitivity
      if (
        sortedKeys
          .map((val) => val.toLowerCase())
          .indexOf(key.toLowerCase()) !== index
      ) {
        // Remove the key that is not all lowercase
        if (key.toLowerCase() !== key) {
          prevValue.push(key);
        }
      } else {
        prevValue.push(key);
      }
      return prevValue;
    },
    []
  );
};

export const getModalCategoriesToShow = (
  selectionMap: any,
  indeterminates: Set<string>
): string[] => {
  const selectionMapKeys: string[] = [];

  Array.from(selectionMap.keys()).forEach((selectionMapKey: any) => {
    if (
      selectionMap.get(selectionMapKey) &&
      !indeterminates.has(selectionMapKey)
    ) {
      selectionMapKeys.push(selectionMapKey);
    }
  });

  const modalCategoriesToShow: string[] = [];

  removeDuplicates(selectionMapKeys.sort()).forEach((sortedKey) => {
    const splitKey = sortedKey.split('/');

    const isKind = splitKey.length === 1;
    const isSubCategory = splitKey.length === 2;
    const isBrandSubType = splitKey.length === 3;

    if (isKind) {
      modalCategoriesToShow.push(sortedKey);
    } else if (
      isSubCategory &&
      modalCategoriesToShow.indexOf(`${splitKey[0]}`) === -1
    ) {
      modalCategoriesToShow.push(sortedKey);
    } else if (
      // if the length is 3, make sure the previous 2 strings in the array when concat'd together aren't already in array
      isBrandSubType &&
      modalCategoriesToShow.indexOf(`${splitKey[0]}`) === -1 &&
      modalCategoriesToShow.indexOf(`${splitKey[0]}/${splitKey[1]}`) === -1
    ) {
      modalCategoriesToShow.push(sortedKey);
    }
  });

  return modalCategoriesToShow;
};

export const convertCategoryConditionToRules = (
  input: string[] | { label: string; value: string }[]
) => {
  if (!input?.length) return [];

  const rulesInput: any = input.map(
    (item: string | { label: string; value: string }) => {
      const parts = decodeKey(typeof item === 'string' ? item : item.value);

      if (parts.length === 1) {
        return { kind: parts[0] };
      } else if (parts.length === 2) {
        return { kind: parts[0], root_subtype: parts[1] };
      } else if (parts.length === 3) {
        return {
          kind: parts[0],
          root_subtype: parts[1],
          brand_subtype: parts[2],
        };
      }

      return {};
    }
  );
  return rulesInput;
};
